import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Mail, MapPin, Phone } from 'lucide-react';
import SocialLinks from './SocialLinks';
import Button from './ui/Button';
import { SITE } from '../data/site';

/**
 * C14 footer on the light grey ground: Contact / Hours / Address columns with
 * lucide line icons, a click-to-load map, then logo, quick links, legal links,
 * social links, copyright and the PX Docs credential line (D9, text only).
 *
 * App.tsx renders <Footer.FooterMap showMap /> inside #containing_wrap and
 * <Footer.CreditBar /> after it. The whole footer now renders in FooterMap;
 * CreditBar renders nothing (kept so the Layout does not change).
 */

const QUICK_LINKS: Array<[string, string]> = [
  ['/meet-the-doctors/', 'Meet Our Doctors'],
  ['/our-services/', 'Our Services'],
  ['/new-patient-center/', 'New Patient Center'],
  ['/we-can-help/', 'Conditions We Help'],
  ['/patient-reviews/', 'Reviews'],
  ['/reviews/', 'Google Reviews'],
  ['/blog/', 'Blog'],
  ['/service-areas/', 'Communities We Serve'],
];

const LEGAL_LINKS: Array<[string, string, boolean]> = [
  ['/info/copyright/', 'Copyright', true],
  ['/info/legal-disclaimer/', 'Legal', true],
  ['/privacy/', 'Privacy', true],
  ['/terms-of-service/', 'Terms of Service', true],
  ['/info/accessibility/', 'Accessibility', true],
  ['/sitemap/', 'Sitemap', false],
];

const MAP_EMBED =
  'https://www.google.com/maps?q=Van%20Every%20Family%20Chiropractic%20Center%204203%20Rochester%20Rd%20Royal%20Oak%20MI%2048073&output=embed';
const MAP_LINK = 'https://www.google.com/maps/search/?api=1&query=Van+Every+Family+Chiropractic+Center+4203+Rochester+Rd+Royal+Oak+MI+48073';

function splitHours(entry: string): [string, string] {
  const i = entry.indexOf(':');
  return [entry.slice(0, i), entry.slice(i + 2)];
}

function FooterMapEmbed({ eager }: { eager: boolean }) {
  const [loaded, setLoaded] = useState(eager);
  return (
    <div className="ve-footer-map">
      {loaded ? (
        <iframe
          src={MAP_EMBED}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Map to Van Every Family Chiropractic Center"
        />
      ) : (
        <button type="button" className="ve-footer-map-btn" onClick={() => setLoaded(true)}>
          <MapPin aria-hidden="true" focusable="false" />
          Show map
        </button>
      )}
      <a className="ve-footer-map-link" href={MAP_LINK} target="_blank" rel="noopener noreferrer">
        Open in Google Maps
      </a>
    </div>
  );
}

function FooterMap({ showMap = false }: { showMap?: boolean }) {
  const year = new Date().getFullYear();
  return (
    <footer className="ve-footer" role="contentinfo">
      <div className="ve-container ve-container--wide">
        <h2 className="sr-only">Visit Van Every Family Chiropractic Center</h2>
        <div className="ve-footer-grid">
          <div className="ve-footer-col ve-footer-contact">
            <h2>
              <Phone aria-hidden="true" focusable="false" />
              Contact
            </h2>
            <p>
              <strong>Call:</strong> <a href={`tel:${SITE.phone}`}>{SITE.phoneDisplay}</a>
            </p>
            <p>
              <strong>Email:</strong>{' '}
              <a href={`mailto:${SITE.email}`}>
                <Mail aria-hidden="true" focusable="false" style={{ width: 16, height: 16, verticalAlign: '-3px', marginRight: 6 }} />
                {SITE.email}
              </a>
            </p>
            <div className="ve-footer-buttons">
              <Button variant="outline-navy" to="/contact-us/">
                Contact us
              </Button>
              <Button to={SITE.booking.chooserPath}>
                Book Online
              </Button>
            </div>
            <p className="ve-small">Gentle, nervous-system focused chiropractic care for babies, kids, expecting moms and the whole family in Royal Oak.</p>
          </div>

          <div className="ve-footer-col">
            <h2>
              <Clock aria-hidden="true" focusable="false" />
              Office Hours
            </h2>
            <ul className="ve-footer-hours">
              {SITE.hours.display.map((entry) => {
                const [day, hours] = splitHours(entry);
                return (
                  <li key={day}>
                    <span>{day}</span>
                    <span>{hours}</span>
                  </li>
                );
              })}
              <li className="is-closed">
                <span>Sunday</span>
                <span>Closed</span>
              </li>
            </ul>
          </div>

          <div className="ve-footer-col ve-footer-address">
            <h2>
              <MapPin aria-hidden="true" focusable="false" />
              Our Address
            </h2>
            <address>
              <strong>Van Every Family Chiropractic Center</strong>
              <br />
              4203 Rochester Rd
              <br />
              Royal Oak, MI 48073
            </address>
            <FooterMapEmbed eager={false} key={showMap ? 'home' : 'inner'} />
          </div>
        </div>

        <div className="ve-footer-bottom" id="utility_credit_wrap">
          <Link to="/" className="ve-footer-logo" aria-label="Van Every Family Chiropractic Center, home">
            <img src="/images/logo2-w300.webp" srcSet="/images/logo2-w300.webp 300w, /images/logo2.webp 477w" sizes="150px" alt="Van Every Family Chiropractic Center" width="477" height="185" loading="lazy" decoding="async" />
          </Link>
          <ul className="ve-footer-legal">
            {LEGAL_LINKS.map(([to, label, nofollow]) => (
              <li key={to}>
                <Link to={to} rel={nofollow ? 'nofollow' : undefined}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <ul className="ve-footer-quick" aria-label="Popular pages">
          {QUICK_LINKS.map(([to, label]) => (
            <li key={to}>
              <Link to={to}>{label}</Link>
            </li>
          ))}
        </ul>
        <div className="ve-footer-fine">
          <p>
            &copy; {year} Van Every Family Chiropractic Center. A PX office serving Royal Oak families.
          </p>
          <SocialLinks className="ve-footer-social" />
        </div>
        <p className="sr-only">
          <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
        </p>
      </div>
    </footer>
  );
}

/** Kept for App.tsx; the legal row now renders inside FooterMap. */
function CreditBar() {
  return null;
}

const Footer = { FooterMap, CreditBar };
export default Footer;
